import { logOut } from 'actions/userActions';
import AppIcon from 'components/common/AppIcon/AppIcon';
import { PENDING_USER_STATUS } from 'constants/userStatuses';
import { upperFirst } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { colors, navPanelWidth } from 'styles/shared';
import { Nav, OverlayTrigger, Tooltip } from './bootstrap';

const NavigationContainer = styled.div`
  position: absolute;
  width: ${navPanelWidth};
  left: 0;
  top: 0;
  height: 100%;
  background: ${colors.blue_lighter};
  padding: 3.4rem 0 5.5rem;
`;

const LogoContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 9rem;
  margin-right: 3.6rem;
`;

const NavigationPanel = styled.div`
  margin-left: ${props => (props.small ? 0 : '6.4rem')};

  a {
    font-weight: bold;
    font-size: 1.8rem;
    line-height: 2.2rem;
    color: ${colors.darkBlue_light};
    display: flex;
    align-items: center;
    margin-bottom: 3rem;

    &.active {
      color: ${colors.darkBlue_darker};
      border-right: 0.3rem solid ${colors.blue_darker};
    }
  }
`;

const SignOutLink = styled(NavigationPanel)`
  position: absolute;
  bottom: 5.5rem;
  width: 100%;

  a {
    margin: 0;
  }
`;

const IconContainer = styled.span`
  width: ${props => (props.small ? '100%' : '4.2rem')};
  display: flex;
  justify-content: center;

  > div {
    padding: 0 0.5rem;
  }
`;

Navigation.propTypes = {
  small: PropTypes.bool
};

Navigation.defaultProps = {
  small: true
};

function Navigation({small}) {
  let dispatch = useDispatch();
  let history = useHistory();
  let location = useLocation();

  const user = useSelector((state: any) => state.user.current);

  const showTooltips = small;

  async function onLogOut() {
    history.push('/login');

    await dispatch(logOut());
  }

  function renderSignOutSection() {
    const text = 'Sign out';

    const icon = <AppIcon name="sign-out" />;

    const tooltip = <Tooltip id="sign-out-tooltip">{text}</Tooltip>;

    return (
      <SignOutLink small={small}>
        <Nav.Link href="#" onClick={onLogOut}>
          <IconContainer small={small}>
            {showTooltips ? (
              <OverlayTrigger placement="top" overlay={tooltip}>
                <div>{icon}</div>
              </OverlayTrigger>
            ) : (
              icon
            )}
          </IconContainer>

          {!small && <span>{text}</span>}
        </Nav.Link>
      </SignOutLink>
    );
  }

  function renderLink(path, icon = '') {
    let iconName = icon || path;

    let title = path || icon;

    path = `/${path}`;

    let active = location.pathname === path;

    let color = active ? colors.blue_darker : colors.blue_main;

    const appIcon = <AppIcon name={iconName} color={color} />;

    let displayTitle = upperFirst(title);

    const tooltip = <Tooltip id={title}>{displayTitle}</Tooltip>;

    return (
      <Nav.Link as={Link} href={path} to={path} active={active}>
        <IconContainer small={small}>
          {showTooltips ? (
            <OverlayTrigger placement="top" overlay={tooltip}>
              <div>{appIcon}</div>
            </OverlayTrigger>
          ) : (
            appIcon
          )}
        </IconContainer>

        {!small && <span>{displayTitle}</span>}
      </Nav.Link>
    );
  }

  function render() {
    let isPendingStatus = user?.status === PENDING_USER_STATUS;
    let isSubscribed = (user?.subscription_id !== '' && user?.subscription_id !== null) || user?.trial_requests > 0;
    let isAdmin = user?.isAdmin;

    let hidePages = isPendingStatus || !isSubscribed;

    let hideAdminPage = isPendingStatus || !isAdmin;

    return (
      <NavigationContainer id="navigation-panel">
        <LogoContainer>
          <AppIcon name="logo" />
        </LogoContainer>

        <NavigationPanel small={small}>
          {renderLink('', 'home')}
          {!hidePages && renderLink('explore')}
          {renderLink('trips')}
          {!hidePages && renderLink('billing')}
          {!isPendingStatus && !isSubscribed && renderLink('subscribe', 'billing')}
          {renderLink('support')}
          {renderLink('docs')}
          {renderLink('about')}
          {!hideAdminPage && renderLink('admin', 'settings')}
        </NavigationPanel>

        {renderSignOutSection()}
      </NavigationContainer>
    );
  }

  return render();
}

export default Navigation;
